import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { config } from "../constants/config";
import { buildS3Url } from "../utils/pathRegistryUtils";
import { addVoiceSnippet } from "./voiceSnippetSlice";
import { addTextSnippet } from "./textSnippetSlice";
import { addCharacterSpeechSample, getHighestRevision, triggerReRender } from "./charSamplesSlice";
import DOMPurify from "dompurify";

// Utility function to sanitize and encode HTML content
const sanitizeAndEncodeHtml = async (htmlContent) => {
  if (!htmlContent) return '';

  // Sanitize HTML
  const sanitizedHtml = DOMPurify.sanitize(htmlContent, { USE_PROFILES: { html: true } });

  // Encode HTML to Base64
  try {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(sanitizedHtml);
    const blob = new Blob([encoded], { type: 'text/plain; charset=utf-8' });

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result.split(',')[1]; // Removes the Data URL part
        resolve(base64data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.error('Error encoding HTML:', e);
    return '';
  }
};

// Utility function to upload text snippet to S3
const uploadTextSnippetToS3 = async (userId, textSnippet, fileName) => {
  try {
    const response = await axios.post(
      `${config.UPLOAD_API_TEXT_SNIPPET}/${userId}/${fileName}`,
      { textSnippet }
    );
    return response.data.fileUrl;
  } catch (error) {
    console.error('Error uploading text snippet to S3:', error);
    throw error;
  }
};

// Globals for mediaRecorder and audio chunks
let mediaRecorderInstance = null;
let audioChunksRef = [];

// Expose audioChunksRef globally for the RecordVariationModal component
window.audioChunksRef = audioChunksRef;

// AsyncThunk: Start Recording
export const startRecording = createAsyncThunk(
  "recording/startRecording",
  async (_, { dispatch }) => {
    try {
      // Clear any existing audio chunks before starting a new recording
      audioChunksRef = [];
      window.audioChunksRef = audioChunksRef; // Update the global reference
      dispatch(updateChunksLength(0));

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderInstance = new MediaRecorder(stream);

      // Capture audio chunks and update Redux state
      mediaRecorderInstance.addEventListener("dataavailable", (event) => {
        if (event.data.size > 0) {
          audioChunksRef.push(event.data);
          window.audioChunksRef = audioChunksRef; // Update the global reference
          dispatch(updateChunksLength(audioChunksRef.length));
        }
      });

      mediaRecorderInstance.start(1000); // Emit chunks every 1 second
      dispatch(setRecordingStatus("recording"));
    } catch (error) {
      console.error("Error starting recording:", error);
      dispatch(setRecordingStatus("error"));
    }
  }
);

// AsyncThunk: Stop Recording
export const stopRecording = createAsyncThunk(
  "recording/stopRecording",
  async (_, { dispatch }) => {
    if (mediaRecorderInstance) {
      // Get the stream and its tracks
      const stream = mediaRecorderInstance.stream;

      mediaRecorderInstance.stop();
      mediaRecorderInstance.ondataavailable = null; // Remove the event listener

      // Stop all tracks in the stream to release the microphone
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      mediaRecorderInstance = null; // Clear the instance
      dispatch(setRecordingStatus("stopped"));
    }
  }
);
export const pauseRecording = createAsyncThunk(
  "recording/pauseRecording",
  async (_, { dispatch }) => {
    console.log("recording/pauseRecording state:", mediaRecorderInstance?.state);

    if (mediaRecorderInstance && mediaRecorderInstance.state === "recording") {
      mediaRecorderInstance.pause();
      dispatch(setRecordingStatus("paused"));
    } else {
      console.error("No active recording to pause");
    }
  }
);

export const resumeRecording = createAsyncThunk(
  "recording/resumeRecording",
  async (_, { dispatch }) => {
    console.log("recording/resumeRecording");
    if (mediaRecorderInstance && mediaRecorderInstance.state === "paused") {
      mediaRecorderInstance.resume();
      dispatch(setRecordingStatus("recording"));
    } else {
      console.error("No paused recording to resume");
    }
  }
);

// AsyncThunk: Save Recording
// activeElement { char_uuid: xxx, char_name: "someone"}
// revision (charSpeechData.speechSample.revision)
// variation (charSpeechData.variation)
// literaryGroup (snippetDetails.details.values.literaryGroup)
// charUuid: charSpeechData.character.uuid)
// highestRevision
// newFileName = `${variation}_V${revision + 1}.wav`

// Initialize recording utils with data from other parts of the state
export const initializeRecordingUtils = createAsyncThunk(
  "recording/initializeRecordingUtils",
  async (params = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState();
      const source = params.source || 'default';

      console.log(`Initializing recording utils with source: ${source}`);

      // Get user ID from profile with validation
      const userId = state.user.profile?.data?.userId;
      if (!userId) {
        console.error("Missing userId in user profile");
        return rejectWithValue("Missing userId in user profile");
      }
      dispatch(setUserId(userId));

      // Get the current state to check if we already have targetData
      const currentState = getState();
      const recordingUtils = currentState.user.recordingUtils;
      console.log('initialize recordingUtils', recordingUtils);

      // If we already have targetData, use it instead of creating a new one
      if (recordingUtils.targetData) {
        console.log("Using existing targetData:", recordingUtils.targetData);

        // Destructure the nested character path for cleaner code
        const characterPath = recordingUtils.targetData.character?.character?.character;

        // Validate critical targetData properties
        if (!characterPath) {
          console.error("Missing character in targetData");
          return rejectWithValue("Missing character in targetData");
        }

        if (!characterPath.uuid) {
          console.error("Missing character UUID in targetData");
          return rejectWithValue("Missing character UUID in targetData");
        }
      }
      // Otherwise, create targetData from charSpeechData if available
      else {
        // Get charSpeechData based on source
        let charSpeechData;
        if (source === 'voiceActor') {
          // For voice actor, we already have the data in recordingUtils
          charSpeechData = recordingUtils.charSpeechData;
        } else {
          // Default: Get from userLitGroupAssets
          charSpeechData = state.user.userLitGroupAssets?.charSpeechData;
        }

        if (charSpeechData) {
          console.log("Creating targetData from charSpeechData:", charSpeechData);

          // Validate critical charSpeechData properties
          if (!charSpeechData.character) {
            console.error("Missing character in charSpeechData");
            return rejectWithValue("Missing character in charSpeechData");
          }

          if (!charSpeechData.character.uuid) {
            console.error("Missing character UUID in charSpeechData");
            return rejectWithValue("Missing character UUID in charSpeechData");
          }

          // Set targetData with validated charSpeechData
          const targetData = {
            character: charSpeechData
          };
          dispatch(setTargetData(targetData));
          dispatch(setCharSpeechData(charSpeechData));
        } else {
          console.error(`Missing charSpeechData from ${source}`);
          return rejectWithValue(`Missing charSpeechData from ${source}`);
        }
      }

      // Get snippetDetails based on source
      let snippetDetails;
      if (source === 'voiceActor') {
        // For voice actor, get from recordingUtils slice
        snippetDetails = recordingUtils.snippetDetails;
        console.log("Using snippetDetails from recordingUtils:", snippetDetails);
      } else {
        // Default: Get from userLitGroupAssets
        snippetDetails = state.user.userLitGroupAssets?.voiceFormValues;
        console.log("Using snippetDetails from userLitGroupAssets:", snippetDetails);
      }

      if (!snippetDetails) {
        console.error(`Missing snippetDetails from ${source}`);
        return rejectWithValue(`Missing snippetDetails from ${source}`);
      }

      // Validate critical snippetDetails properties
      if (!snippetDetails.details?.values) {
        console.error("Missing details.values in snippetDetails");
        return rejectWithValue("Missing details.values in snippetDetails");
      }

      if (!snippetDetails.details.values.literaryGroup) {
        console.error("Missing literaryGroup in snippetDetails");
        return rejectWithValue("Missing literaryGroup in snippetDetails");
      }

      if (!snippetDetails.details.values.litGroupBook) {
        console.error("Missing litGroupBook in snippetDetails");
        return rejectWithValue("Missing litGroupBook in snippetDetails");
      }

      // Only set snippetDetails if we're not using the voice actor source
      // For voice actor, we've already set it in the RecordingControls component
      if (source !== 'voiceActor') {
        dispatch(setSnippetDetails(snippetDetails));
      }

      // Get the updated state after all dispatches
      const updatedState = getState();
      const updatedRecordingUtils = updatedState.user.recordingUtils;

      console.log("Recording utils initialized successfully with:", {
        userId,
        source,
        targetData: updatedRecordingUtils.targetData,
        snippetDetails,
      });

      return { success: true };
    } catch (error) {
      console.error("Error initializing recording utils:", error);
      return rejectWithValue("Error initializing recording utils: " + error.message);
    }
  }
);

export const saveRecording = createAsyncThunk(
  "recording/saveRecording",
  async (params = {}, { dispatch, getState }) => {
    const state = getState();
    const source = params.source || 'default';

    try {
      // First, try to initialize recording utils if needed
      await dispatch(initializeRecordingUtils({ source })).unwrap();

      // Get the updated state after initialization
      const updatedState = getState();
      const recordingUtils = updatedState.user.recordingUtils;

      // Log the state for debugging
      console.log("Recording utils state:", recordingUtils);

      // Check if we have audio chunks
      if (!audioChunksRef || audioChunksRef.length === 0) {
        console.error("No audio chunks available to save.");
        dispatch(setRecordingStatus("error"));
        return;
      }

      // Comprehensive validation of required data
      if (!recordingUtils.userId) {
        console.error("Missing userId in recordingUtils");
        dispatch(setRecordingStatus("error"));
        return;
      }

      if (!recordingUtils.targetData) {
        console.error("Missing targetData in recordingUtils");
        dispatch(setRecordingStatus("error"));
        return;
      }

      if (!recordingUtils.snippetDetails) {
        console.error("Missing snippetDetails in recordingUtils");
        dispatch(setRecordingStatus("error"));
        return;
      }

      // Validate nested properties that will be used
      const { userId, targetData, snippetDetails } = recordingUtils;

      // Destructure the nested character path for cleaner code
      const characterPath = targetData.character?.character?.character;
      const speechSnippetContent = targetData.textSnippet?.content;

      // Validate character data
      if (!characterPath) {
        console.error("Missing character in targetData");
        dispatch(setRecordingStatus("error"));
        return;
      }

      const characterData = characterPath;
      if (!characterData.uuid) {
        console.error("Missing character UUID in targetData");
        dispatch(setRecordingStatus("error"));
        return;
      }

      if (!snippetDetails.details?.values) {
        console.error("Missing details.values in snippetDetails");
        dispatch(setRecordingStatus("error"));
        return;
      }

      if (!snippetDetails.details.values.literaryGroup) {
        console.error("Missing literaryGroup in snippetDetails");
        dispatch(setRecordingStatus("error"));
        return;
      }

      if (!snippetDetails.details.values.litGroupBook) {
        console.error("Missing litGroupBook in snippetDetails");
        dispatch(setRecordingStatus("error"));
        return;
      }

      // Create the audio blob
      const audioBlob = new Blob(audioChunksRef, { type: "audio/wav" });

      // Determine the file name with standardized approach to variation handling
      let variationName = "default";
      let revision = 0;
      let variationUuid = characterData.uuid; // Default to character UUID

      // Get variation from targetData
      if (targetData.variation) {
        variationName = targetData.variation;
      }

      // Find the variation in the variations array if it exists
      if (targetData.character?.character?.variations) {
        const variations = targetData.character.character.variations;
        const currentVariation = variations.find(v => v.variation.name === variationName);

        if (currentVariation) {
          variationUuid = currentVariation.variation.uuid || characterData.uuid;

          // Find the speech sample for this variation
          if (currentVariation.speechSamples && currentVariation.speechSamples.length > 0) {
            const speechSample = currentVariation.speechSamples[0].speechSample;
            if (speechSample && typeof speechSample.revision === 'string') {
              revision = parseInt(speechSample.revision, 10) || 0;
            }
          }
        }
      }

      // Construct parameters for highest revision with all required fields
      const highestRevisionParams = {
        userId,
        litGroup: snippetDetails.details.values.literaryGroup,
        bookData: {
          bookTree: {
            uuid: snippetDetails.details.values.litGroupBook
          }
        },
        charUuid: characterData.uuid,
        variationUuid: variationUuid,
      };

      console.log("Getting highest revision with params:", highestRevisionParams);

      const highestRevisionResponse = await dispatch(
        getHighestRevision(highestRevisionParams)
      ).unwrap();

      const newVersionNo = highestRevisionResponse.highestRevision + 1;
      console.log("New version number:", newVersionNo);

      // Generate file names for both audio and text
      const formattedFileName = `${variationName}_V${newVersionNo}.wav`;
      const formattedTextFileName = `${variationName}_V${newVersionNo}.txt`;

      console.log("Saving recording with filename:", formattedFileName);

      // Generate a common UUID for both voice and text snippets
      const uuid = uuidv4();
      const speechSampleUuid = uuidv4();

      // Get title and enabled status from snippetDetails
      const { title, enabled } = snippetDetails.snippetValues?.values || { title: "Untitled", enabled: true };
      const dorkTitle = `${characterPath.name} - ${targetData.character.variation}`;
      // 1. Process and upload audio file
      const audioFormData = new FormData();
      audioFormData.append("audio", audioBlob, formattedFileName);

      // Add voice actor specific parameters if available
      const actorId = params.actorId;
      const portfolioId = params.portfolioId;
      const characterId = params.characterId;
      const variationId = params.variationId;

      let audioResponse;

      // If this is a voice actor variation recording, use the new endpoint
      if (source === 'voiceActor' && actorId && portfolioId && characterId) {
        console.log("Using voice actor variation endpoint with IDs:", {
          actorId,
          portfolioId,
          characterId,
          variationId
        });

        // Check if variationId is a valid UUID (not undefined or a placeholder)
        // UUID format: 8-4-4-4-12 hexadecimal digits
        const isValidVariationId = variationId && typeof variationId === 'string' &&
          (variationId !== 'new-variation' && variationId !== 'default-variation') &&
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(variationId);

        if (!isValidVariationId) {
          console.log("No valid variationId provided, using regular upload endpoint");
          // Use the existing endpoint for regular recordings
          audioResponse = await axios.post(
            `${config.UPLOAD_API_AUDIO_SNIPPET}/${userId}`,
            audioFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        } else {
          // Add the IDs to the form data
          audioFormData.append("userId", userId);
          audioFormData.append("actorId", actorId);
          audioFormData.append("portfolioId", portfolioId);
          audioFormData.append("characterId", characterId);
          audioFormData.append("variationId", variationId);

          console.log("Using variation-specific endpoint with variationId:", variationId);
          const url = config.UPLOAD_VOICE_ACTOR_VARIATION_AUDIO.replace(':id', variationId) +
            `?userId=${userId}&actorId=${actorId}&portfolioId=${portfolioId}&characterId=${characterId}&variationId=${variationId}`;
          audioResponse = await axios.post(
            url,
            audioFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
        }
        // Use the new endpoint for voice actor variations
        // audioResponse = await axios.post(
        //   `${config.BASE_URL}/character-variations/${variationId}/audio`,
        //   audioFormData,
        //   { headers: { "Content-Type": "multipart/form-data" } }
        // );

        // If the server doesn't return a valid S3 URL, construct it manually
        if (!audioResponse.data.url && !audioResponse.data.fileUrl) {
          // Use a valid UUID for the S3 URL, not the potentially invalid variationId
          const effectiveVariationId = isValidVariationId ? variationId : uuidv4();
          console.log("Server did not return S3 URL, constructing manual S3 URL with effectiveVariationId:", effectiveVariationId);

          // Build the manual S3 URL via the PathRegistry (single source of
          // truth) using PathRef `actVariationVoice`.
          const s3Url = await buildS3Url(
            "actVariationVoice",
            {
              userId,
              actorId,
              portfolioId,
              characterId,
              variationId: effectiveVariationId,
            },
            formattedFileName
          );
          audioResponse.data.url = s3Url;
        }
      } else {
        // Use the existing endpoint for regular recordings
        audioResponse = await axios.post(
          `${config.UPLOAD_API_AUDIO_SNIPPET}/${userId}`,
          audioFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      }

      const s3Url = audioResponse.data.url || audioResponse.data.fileUrl;

      // Store the S3 URL in the state for later use
      dispatch(setLastRecordingUrl(s3Url));

      // 2. Process and upload text snippet
      // Sanitize and encode the editor content
      const editorContent = speechSnippetContent || ""; //snippetDetails.snippetValues?.editorContent || "";
      const encoded = await sanitizeAndEncodeHtml(editorContent);
      const s3TextUrl = await uploadTextSnippetToS3(userId, encoded, formattedTextFileName);

      // 3. Save voice snippet metadata to Neo4j
      await dispatch(
        addVoiceSnippet({
          userId,
          fileName: formattedFileName,
          variation: variationName,
          s3Url: s3Url,
          version: newVersionNo,
          uuid: uuid,
        })
      );

      // 4. Save text snippet metadata to Neo4j
      await dispatch(
        addTextSnippet({
          userId,
          fileName: formattedTextFileName,
          variation: variationName,
          s3TextUrl: s3TextUrl,
          version: newVersionNo,
          uuid: uuid,
        })
      );

      // 5. Create character speech sample
      const characterSpeechSample = {
        title: dorkTitle, //title,
        lit_group_uuid: snippetDetails.details.values.literaryGroup,
        book_uuid: snippetDetails.details.values.litGroupBook,
        char_speech_uuid: speechSampleUuid,
        character_uuid: characterData.uuid,
        variation_uuid: variationUuid,
        variation_name: variationName,
        textSnippet_uuid: uuid,
        voiceSnippet_uuid: uuid,
        revision: newVersionNo,
        enabled: enabled,
        isSelected: false
      };

      await dispatch(addCharacterSpeechSample({
        userId,
        ...characterSpeechSample
      }));

      // We'll refresh the data only after the recording is completed
      // This will be handled by the refreshCharacterData thunk

      // Clear the recording state and trigger a re-render
      dispatch(clearRecordingState());
      dispatch(triggerReRender());

      console.log("Recording saved successfully");
      dispatch(setRecordingStatus("completed"));

      // Now that the recording is completed, refresh the character data
      // This ensures we only refresh once at the end of the save process
      await dispatch(refreshCharacterData());
    } catch (error) {
      console.error("Error saving recording:", error);
      dispatch(setRecordingStatus("error"));
      // Provide more detailed error information
      if (error.response) {
        console.error("Server response error:", error.response.data);
      }
    }
  }
);

// AsyncThunk to refresh character data
export const refreshCharacterData = createAsyncThunk(
  "recording/refreshCharacterData",
  async (params = {}, { dispatch, getState }) => {
    try {
      // Get parameters from the state if not provided directly
      const state = getState();
      const userId = params.userId || state.user.profile?.data?.userId;

      // Try to get snippetDetails from the state if not provided
      let litGroupUuid, bookUuid;

      if (params.litGroupUuid && params.bookUuid) {
        // Use provided parameters
        litGroupUuid = params.litGroupUuid;
        bookUuid = params.bookUuid;
      } else {
        // Try to get from state
        const snippetDetails = state.user.recordingUtils?.snippetDetails;

        if (snippetDetails?.details?.values) {
          litGroupUuid = snippetDetails.details.values.literaryGroup;
          bookUuid = snippetDetails.details.values.litGroupBook;
        }
      }

      // If we still don't have the required parameters, log an error and return
      if (!userId) {
        console.error("Missing userId for refreshing character data");
        return;
      }

      // If we have litGroupUuid and bookUuid, refresh the character variation samples
      if (litGroupUuid && bookUuid) {
        console.log("Refreshing character variation samples...");

        // Import the fetchBookCharsVariationSamples action from userLitGroupAssetsSlice
        const { fetchBookCharsVariationSamples } = await import('./userLitGroupAssetsSlice');

        // Dispatch the action to refresh the character data
        await dispatch(fetchBookCharsVariationSamples({
          litGroupUuid,
          bookUuid
        })).unwrap();
      }

      // Always refresh the character data in the aaiCharSequencerSlice
      console.log("Refreshing character data in sequencer...");
      const { fetchInitialData } = await import('./aaiCharSequencerSlice');

      // Dispatch the fetchInitialData action to refresh the character data in the sequencer
      await dispatch(fetchInitialData({
        userId,
        jsonFile: params.jsonFile || null
      })).unwrap();

      console.log("Character data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing character data:", error);
    }
  }
);

// AsyncThunk: Delete Recording
export const deleteRecording = createAsyncThunk(
  "recording/deleteRecording",
  async (_, { dispatch }) => {
    audioChunksRef = [];
    mediaRecorderInstance = null;
    dispatch(clearRecordingState());
  }
);


export const setActiveTarget = createAsyncThunk(
  "recording/setActiveTarget",
  async (_, { dispatch, getState }) => {
    try {
      const state = getState();
      const { targetData, snippetDetails, userId } = state.user.recordingUtils;

      // Comprehensive validation of required data
      if (!userId) {
        console.error("Missing userId in recordingUtils");
        return;
      }

      if (!targetData) {
        console.error("Missing targetData in recordingUtils");
        return;
      }

      if (!snippetDetails) {
        console.error("Missing snippetDetails in recordingUtils");
        return;
      }

      // Destructure the nested character path for cleaner code
      const characterPath = targetData.character?.character?.character;

      // Validate character data
      if (!characterPath) {
        console.error("Missing character in targetData");
        return;
      }

      const characterData = characterPath;
      if (!characterData.uuid) {
        console.error("Missing character UUID in targetData");
        return;
      }

      if (!snippetDetails.details?.values) {
        console.error("Missing details.values in snippetDetails");
        return;
      }

      if (!snippetDetails.details.values.literaryGroup) {
        console.error("Missing literaryGroup in snippetDetails");
        return;
      }

      if (!snippetDetails.details.values.litGroupBook) {
        console.error("Missing litGroupBook in snippetDetails");
        return;
      }

      // Determine variation name and UUID with standardized approach
      let variationName = "default";
      let revision = 0;
      let variationUuid = characterData.uuid; // Default to character UUID

      // Get variation from targetData
      if (targetData.variation) {
        variationName = targetData.variation;
      }

      // Find the variation in the variations array if it exists
      if (targetData.character?.character?.variations) {
        const variations = targetData.character.character.variations;
        const currentVariation = variations.find(v => v.variation.name === variationName);

        if (currentVariation) {
          variationUuid = currentVariation.variation.uuid || characterData.uuid;

          // Find the speech sample for this variation
          if (currentVariation.speechSamples && currentVariation.speechSamples.length > 0) {
            const speechSample = currentVariation.speechSamples[0].speechSample;
            if (speechSample && typeof speechSample.revision === 'string') {
              revision = parseInt(speechSample.revision, 10) || 0;
            }
          }
        }
      }

      // Fetch the highest revision with validated parameters
      const highestRevisionResponse = await dispatch(
        getHighestRevision({
          userId,
          litGroup: snippetDetails.details.values.literaryGroup,
          bookData: {
            bookTree: {
              uuid: snippetDetails.details.values.litGroupBook
            }
          },
          charUuid: characterData.uuid,
          variationUuid: variationUuid,
        })
      ).unwrap();

      const highestRevision = highestRevisionResponse.highestRevision;
      const newRevision = revision + 1;

      const newFileName = `${variationName}_V${newRevision}.wav`;

      // Update the activeTarget object with validated data
      const updatedActiveTarget = {
        activeElement: characterData,
        revision: newRevision,
        variation: { name: variationName, uuid: variationUuid },
        literaryGroup: snippetDetails.details.values.literaryGroup,
        charUuid: characterData.uuid,
        highestRevision,
        newFileName,
      };

      dispatch(setActiveTargetAction(updatedActiveTarget));
      console.log("Active target set successfully:", updatedActiveTarget);
    } catch (error) {
      console.error("Error setting activeTarget:", error);
      if (error.response) {
        console.error("Server response error:", error.response.data);
      }
    }
  }
);


// Slice

const recordingSlice = createSlice({
  name: "recording",
  initialState: {
    recordingStatus: "idle",
    chunksLength: 0,
    bookStats: null,
    targetData: null,
    snippetDetails: null,
    charSpeechData: null,
    userId: null,
    activeTarget: {
      activeElement: {},
      revision: -1,
      variation: {},
      literaryGroup: {},
      charUuid: "",
      highestRevision: "",
      newFileName: "",
    },
    lastRecordingUrl: null, // Store the URL of the last recording
    error: null,
    initialized: false,
  },
  reducers: {
    setRecordingStatus: (state, action) => {
      state.recordingStatus = action.payload;
    },
    updateChunksLength: (state, action) => {
      state.chunksLength = action.payload;
    },
    setActiveTarget: (state, action) => {
      state.activeTarget = action.payload;
    },
    setBookStats: (state, action) => {
      state.bookStats = action.payload;
    },
    setTargetData: (state, action) => {
      state.targetData = action.payload;
    },
    setSnippetDetails: (state, action) => {
      state.snippetDetails = action.payload;
    },
    setCharSpeechData: (state, action) => {
      state.charSpeechData = action.payload;
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
    },
    clearRecordingState: (state) => {
      state.recordingStatus = "idle";
      state.chunksLength = 0;
      state.bookStats = null;
      state.targetData = null;
      state.snippetDetails = null;
      state.charSpeechData = null;
      state.userId = null;
      state.activeTarget = {
        activeElement: {},
        revision: -1,
        variation: {},
        literaryGroup: {},
        charUuid: "",
        highestRevision: "",
        newFileName: "",
      };
      state.lastRecordingUrl = null;
      state.error = null;
    },
    setLastRecordingUrl: (state, action) => {
      state.lastRecordingUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveRecording.rejected, (state, action) => {
        state.error = action.error.message;
        state.recordingStatus = "error " + action.error.message;
      })
      .addCase(initializeRecordingUtils.fulfilled, (state) => {
        state.initialized = true;
      })
      .addCase(initializeRecordingUtils.rejected, (state, action) => {
        state.error = action.error.message;
        state.initialized = false;
      });
  },
});


// Selectors
export const selectRecordingStatus = (state) => {
  return state.user.recordingUtils.recordingStatus;
}
export const selectAudioChunksLength = (state) => state.user.recordingUtils.chunksLength;
export const selectTargetData = (state) => state.user.recordingUtils.targetData;
export const selectBookStats = (state) => {
  return state.user.recordingUtils.bookStats;
}
export const selectSnippetDetails = (state) => state.user.recordingUtils.snippetDetails;
export const isAudioChunksEmpty = (state) => state.user.recordingUtils.chunksLength === 0;
export const selectActiveTarget = (state) => state.user.recordingUtils.activeTarget;
export const selectLastRecordingUrl = (state) => state.user.recordingUtils.lastRecordingUrl;

// Export Actions
export const {
  setRecordingStatus,
  setTargetData,
  setSnippetDetails,
  setCharSpeechData,
  setBookStats,
  setUserId,
  updateChunksLength,
  clearRecordingState,
  setLastRecordingUrl,
  setActiveTarget: setActiveTargetAction, // Exported action for updating activeTarget
} = recordingSlice.actions;

// Export Reducer
export default recordingSlice.reducer;
